import * as Sentry from '@sentry/vue';
import router from '@/router/index'; // Assuming you use Vue Router

export default {
  install: (app) => {
    if (import.meta.env.VITE_SENTRY_ENABLED === 'true') {
      Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_ENVIRONMENT || 'development',
        release: import.meta.env.VITE_SENTRY_RELEASE,
        debug: import.meta.env.VITE_ENVIRONMENT === 'development', // Enable debug in development
        integrations: [
          Sentry.vueIntegration({
            attachProps: true,
            logErrors: true,
            router,
          }),
          Sentry.browserTracingIntegration(),
          Sentry.browserProfilingIntegration(),
        ],

        // Tracing
        tracesSampleRate: 0.5, //  Capture 50% of the transactions
        trackComponents: true,
        attachProps: true, // Attach Vue component props to error reports

        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,

        // Ignorar errores conocidos y de extensiones de browser
        ignoreErrors: [
          // Errores de extensiones del browser
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
          /^moz-extension:\/\//i,
          // Errores de red genéricos que son esperados
          'Non-Error promise rejection captured',
          // Errores de ResizeObserver que son benignos
          'ResizeObserver loop limit exceeded',
        ],

        // Configurar contexto global con información de versión
        beforeSend(event, hint) {
          // Filtrar errores de autenticación (401)
          if (event.exception) {
            const errorMessage = event.exception.values?.[0]?.value || '';
            const stackTrace = event.exception.values?.[0]?.stacktrace?.frames || [];

            // Filtrar errores relacionados con 401
            if (
              errorMessage.includes('401')
                            || errorMessage.includes('Unauthorized')
                            || errorMessage.includes('Authentication failed')
                            || stackTrace.some((frame) => frame.filename?.includes('AuthenticationService')
                                || frame.filename?.includes('auth'))
            ) {
              return null; // No enviar el evento a Sentry
            }

            // Rate limiting para errores de módulos dinámicos
            if (errorMessage.includes('Failed to fetch dynamically imported module')
                            || errorMessage.includes('error loading dynamically imported module')) {
              const sessionKey = 'sentry-dynamic-import-count';
              const count = parseInt(sessionStorage.getItem(sessionKey) || '0');

              if (count < 2) {
                sessionStorage.setItem(sessionKey, (count + 1).toString());
                return null; // No enviar las primeras 2 ocurrencias
              }
              // Después de 2 ocurrencias, enviar con fingerprint personalizado
              event.fingerprint = ['dynamic-import-error', errorMessage.split(':')[0]];
            }

            // Filtrar errores que provienen de node_modules
            const hasNodeModulesError = stackTrace.some((frame) => {
              const filename = frame.filename || '';
              return filename.includes('node_modules/')
                                   || filename.includes('/node_modules/')
                                   || filename.includes('\\node_modules\\')
                                   || filename.includes('webpack://')
                                   || filename.includes('webpack-internal://');
            });

            if (hasNodeModulesError) {
              // Solo filtrar si TODOS los frames son de node_modules
              // Si hay al menos un frame de nuestro código, enviar el error
              const hasOurCode = stackTrace.some((frame) => {
                const filename = frame.filename || '';
                return !filename.includes('node_modules/')
                                       && !filename.includes('/node_modules/')
                                       && !filename.includes('\\node_modules\\')
                                       && !filename.includes('webpack://')
                                       && !filename.includes('webpack-internal://')
                                       && (filename.includes('/src/') || filename.includes('\\src\\'));
              });

              if (!hasOurCode) {
                return null; // No enviar el evento a Sentry
              }
            }

            // Mejorar fingerprinting para Network Errors
            if (errorMessage.includes('Network Error') || errorMessage.includes('Failed to fetch')) {
              // Agrupar todos los network errors similares
              const requestUrl = event.request?.url || 'unknown';
              event.fingerprint = ['network-error', requestUrl];
            }

            // Fingerprinting para errores de validación de documentos
            if (stackTrace.some((frame) => frame.filename?.includes('ValidateRequestEmployee'))) {
              const formType = event.tags?.form_type || 'unknown';
              event.fingerprint = ['document-validation', formType, errorMessage];
            }
          }

          // Agregar información adicional sobre la versión de la app
          event.tags = {
            ...event.tags,
            app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
            build_env: import.meta.env.VITE_ENVIRONMENT,
          };

          // Detectar feature area del error basado en la URL o stack trace
          const currentPath = window.location.hash || window.location.pathname;
          let featureArea = 'unknown';

          if (currentPath.includes('employee') || currentPath.includes('empleado')) {
            featureArea = 'employee-management';
          } else if (currentPath.includes('request') || currentPath.includes('solicitud')) {
            featureArea = 'request-management';
          } else if (currentPath.includes('document')) {
            featureArea = 'document-processing';
          }

          event.tags = {
            ...event.tags,
            feature_area: featureArea,
          };

          // Agregar información del navegador y dispositivo
          event.contexts = {
            ...event.contexts,
            device: {
              ...event.contexts?.device,
              user_agent: navigator.userAgent,
              screen_resolution: `${screen.width}x${screen.height}`,
              viewport: `${window.innerWidth}x${window.innerHeight}`,
            },
            navigation: {
              current_url: window.location.href,
              current_pathname: window.location.pathname,
              current_search: window.location.search,
              current_hash: window.location.hash,
              referrer: document.referrer,
            },
          };

          return event;
        },
      });

      // Configurar usuario y contexto inicial
      Sentry.setContext('app_info', {
        version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
        environment: import.meta.env.VITE_ENVIRONMENT,
        build_time: new Date().toISOString(),
      });

      // Configurar contexto de navegación
      Sentry.setContext('navigation', {
        current_url: window.location.href,
        current_path: window.location.pathname,
        current_search: window.location.search,
        referrer: document.referrer,
      });

      // Actualizar contexto de navegación en cada cambio de ruta
      router.beforeEach((to, from) => {
        // Agregar breadcrumb de navegación
        Sentry.addBreadcrumb({
          category: 'navigation',
          message: `Navigation from ${from.name || 'unknown'} to ${to.name || 'unknown'}`,
          level: 'info',
          data: {
            from_route: from.name,
            from_path: from.path,
            to_route: to.name,
            to_path: to.path,
            to_params: to.params,
            to_query: to.query,
          },
        });
      });

      router.afterEach((to) => {
        Sentry.setContext('navigation', {
          current_url: window.location.href,
          current_path: to.path,
          current_full_path: to.fullPath,
          route_name: to.name,
          route_params: to.params,
          route_query: to.query,
        });

        // Actualizar tag de feature_area basado en la ruta
        let featureArea = 'unknown';
        const routeName = to.name?.toString() || '';

        if (routeName.includes('employee') || routeName.includes('empleado')) {
          featureArea = 'employee-management';
        } else if (routeName.includes('request') || routeName.includes('solicitud')) {
          featureArea = 'request-management';
        } else if (routeName.includes('document')) {
          featureArea = 'document-processing';
        } else if (routeName.includes('home') || routeName === '/') {
          featureArea = 'home';
        }

        Sentry.setTag('feature_area', featureArea);
        Sentry.setTag('current_route', to.name);
      });

      app.config.errorHandler = (error, instance, info) => {
        // Obtener información del componente
        const componentName = instance?.$options?.name || instance?.$?.type?.name || 'Unknown';
        const componentFile = instance?.$options?.__file || 'Unknown';

        // Obtener información detallada de la ruta actual
        const currentRoute = router.currentRoute?.value;
        const routeInfo = {
          name: currentRoute?.name || 'Unknown',
          path: currentRoute?.path || 'Unknown',
          fullPath: currentRoute?.fullPath || 'Unknown',
          params: currentRoute?.params || {},
          query: currentRoute?.query || {},
        };

        Sentry.captureException(error, {
          tags: {
            component_name: componentName,
            component_file: componentFile,
            vue_lifecycle: info || 'Unknown',
            app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
            route_name: routeInfo.name,
            route_path: routeInfo.path,
          },
          extra: {
            componentName,
            componentFile,
            lifecycleHook: info || 'Unknown',
            componentProps: instance?.$props || {},
            route: routeInfo,
            // Información detallada de la URL actual
            currentUrl: window.location.href,
            currentPathname: window.location.pathname,
            currentSearch: window.location.search,
            currentHash: window.location.hash,
            referrer: document.referrer,
            // Información adicional del navegador
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          },
          level: 'error',
        });
      };
    }
  },
};
